import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Checkbox,
  Icon,
  FormControlLabel,
  Typography
} from '@material-ui/core';
import InstagramIcon from '@material-ui/icons/Instagram';
import { makeStyles } from '@material-ui/core/styles';
import { Formik } from 'formik';
import { updateStylePalette } from 'redux/actions/styles_palette';
import { ChevronDoubleLeft } from 'components/CustomSVG';
import BorderSelectorGrid from './components/BorderSelectorGrid';
import BorderRadiusSelectorGrid from './components/BorderRadiusSelectorGrid';
import ColorSelectorGrid from './components/ColorSelectorGrid';
import FontSizeSelectorGrid from './components/FontSizeSelectorGrid';
import TextFieldSelectorGrid from './components/TextFieldSelectorGrid';
import NumberFormatGrid from './components/NumberFormatGrid';
import TextFieldGrid from './components/TextFieldGrid';
import SwitchGrid from './components/SwitchGrid';
import {
  getParameterByName,
  getFontWeightParameters,
  textTransformValues,
  textAlignValues,
  lineHeightValues,
  listStyleTypes,
  textDecorationValues,
  buttonFontSizeValues,
  fontStyleValues
} from './utils/fonts';


const useStyles = makeStyles({
  expanded_index: {
    '&.Mui-expanded': {
      zIndex: '1'
    }
  }
});

const AdvancedConfigurations = ({onStepChange, onSubmit}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [expandedIndex, setExpandedIndex] = useState('0');
  const [isSaveAsDefault, setSaveAsDefault] = useState(false);

  const {stylePalette} = useSelector((state) => state.styles_palette);
  const userStore = useSelector((state) => state.user?.profile?.stores?.[0]);
  const isRegular = userStore.storeType === 'regular';
  const isPremium = userStore.storeType === 'premium' || userStore.storeType === 'modern_premium';
  const isPremiumPlus = userStore.storeType === 'premium_plus' || userStore.storeType === 'modern_premium_plus';

  const handleExpansionClick = (index) => (_, isExpanded) => {
    setExpandedIndex(isExpanded ? index : false);
  };

  const handleUpdateStylePalette = (values) => dispatch(updateStylePalette(values));

  const definedGoogleFontFamilies = getParameterByName(
    'family',
    stylePalette.typography['theme_design/defined_fonts/google_fonts_string']
  );

  return (
    <Formik
      initialValues={stylePalette}
      enableReinitialize
      onSubmit={(values) => onSubmit(values, isSaveAsDefault)}
    >
      {({
        values,
        handleSubmit,
        setFieldValue
      }) => (
        <form onSubmit={handleSubmit}>
          <div className="my-10">
            <Accordion
              elevation={25}
              className={`mt-4 border-radius-4 overflow-visible ${classes.expanded_index}`}
              expanded={expandedIndex === '1'}
              onChange={handleExpansionClick('1')}
            >
              <AccordionSummary
                expandIcon={<Icon>expand_more</Icon>}
              >
                <Typography variant="h5" className="flex items-center text-brand m-2">
                  <Icon className="mr-2">format_color_fill</Icon>
                  Colors
                </Typography>
              </AccordionSummary>
              <AccordionDetails className="flex-column overflow-visible">
                <ColorSelectorGrid
                  value={values.colors['theme_design/colors/color_primary']}
                  name="colors['theme_design/colors/color_primary']"
                  label="Primary Color"
                  onChange={(color) => setFieldValue('colors[\'theme_design/colors/color_primary\']', color)}
                  InputProps={{
                    value: values.colors['theme_design/colors/color_primary'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.colors['theme_design/colors/color_secondry']}
                  name="colors['theme_design/colors/color_secondry']"
                  label="Secondary Color"
                  onChange={(color) => setFieldValue('colors[\'theme_design/colors/color_secondry\']', color)}
                  InputProps={{
                    value: values.colors['theme_design/colors/color_secondry'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.colors['theme_design/colors/color_additional']}
                  name="colors['theme_design/colors/color_additional']"
                  label="Additional Color"
                  onChange={(color) => setFieldValue('colors[\'theme_design/colors/color_additional\']', color)}
                  InputProps={{
                    value: values.colors['theme_design/colors/color_additional'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.colors['theme_design/colors/color_primary_text_color']}
                  name="colors['theme_design/colors/color_primary_text_color']"
                  label="Primary Text Color"
                  onChange={(color) => setFieldValue('colors[\'theme_design/colors/color_primary_text_color\']', color)}
                  InputProps={{
                    value: values.colors['theme_design/colors/color_primary_text_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
              </AccordionDetails>
            </Accordion>
            <Accordion
              elevation={25}
              className={`mt-4 border-radius-4 overflow-visible ${classes.expanded_index}`}
              expanded={expandedIndex === '2'}
              onChange={handleExpansionClick('2')}
            >
              <AccordionSummary
                expandIcon={<Icon>expand_more</Icon>}
              >
                <Typography variant="h5" className="flex items-center text-brand m-2">
                  <Icon className="mr-2">text_fields</Icon>
                  Typography
                </Typography>
              </AccordionSummary>
              <AccordionDetails className="flex-column">
                <Typography variant="h6" className="ml-3 mb-4">Headings</Typography>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <>
                    <TextFieldSelectorGrid
                      value={values.typography[`theme_design/font/heading${i}_font_family`]}
                      name={`typography['theme_design/font/heading${i}_font_family']`}
                      label={`Heading ${i} Font Family`}
                      onChange={(value) => setFieldValue(`typography['theme_design/font/heading${i}_font_family']`, value)}
                      items={definedGoogleFontFamilies}
                    />
                    <ColorSelectorGrid
                      value={values.typography[`theme_design/font/heading${i}_font_color`]}
                      name={`typography['theme_design/font/heading${i}_font_color']`}
                      label={`Heading ${i} Color`}
                      onChange={(color) => setFieldValue(`typography['theme_design/font/heading${i}_font_color']`, color)}
                      InputProps={{
                        value: values.typography[`theme_design/font/heading${i}_font_color`],
                        readOnly: true,
                        shrink: 'true',
                      }}
                    />
                    <NumberFormatGrid
                      value={values.typography[`theme_design/font/heading${i}_font_size`]}
                      suffix="px"
                      name={`typography['theme_design/font/heading${i}_font_size']`}
                      label={`Heading ${i} Font Size`}
                      onChange={(value) => setFieldValue(`typography['theme_design/font/heading${i}_font_size']`, value)}
                      InputProps={{
                        shrink: 'true',
                      }}
                    />
                    <TextFieldSelectorGrid
                      value={values.typography[`theme_design/font/heading${i}_font_weight`]}
                      name={`typography['theme_design/font/heading${i}_font_weight']`}
                      label={`Heading ${i} Font Weight`}
                      items={values.typography[`theme_design/font/heading${i}_font_family`]
                        ? getFontWeightParameters(
                          values.typography[`theme_design/font/heading${i}_font_family`],
                          stylePalette.typography['theme_design/defined_fonts/google_fonts_string']
                        )
                        : [400]
                      }
                      onChange={(value) => setFieldValue(`typography['theme_design/font/heading${i}_font_weight']`, value)}
                      InputProps={{
                        value: values.typography[`theme_design/font/heading${i}_font_weight`],
                        shrink: 'true',
                      }}
                    />
                    <TextFieldSelectorGrid
                      value={values.typography[`theme_design/font/heading${i}_text_transform`]}
                      name={`typography['theme_design/font/heading${i}_text_transform']`}
                      label={`Heading ${i} Text Transform`}
                      items={textTransformValues}
                      onChange={(value) => setFieldValue(`typography['theme_design/font/heading${i}_text_transform']`, value)}
                      InputProps={{
                        value: values.typography[`theme_design/font/heading${i}_text_transform`],
                        shrink: 'true',
                      }}
                    />
                    <TextFieldSelectorGrid
                      value={values.typography[`theme_design/font/heading${i}_line_height`]}
                      name={`typography['theme_design/font/heading${i}_line_height']`}
                      label={`Heading ${i} Line Height`}
                      items={lineHeightValues}
                      onChange={(value) => setFieldValue(`typography['theme_design/font/heading${i}_line_height']`, value)}
                      InputProps={{
                        value: values.typography[`theme_design/font/heading${i}_line_height`],
                        shrink: 'true',
                      }}
                    />
                  </>
                ))}
                <Typography variant="h6" className="ml-3 mb-4">Paragraph Styles</Typography>
                <TextFieldSelectorGrid
                  value={values.typography['theme_design/font/paragraph_font_family']}
                  name="typography['theme_design/font/paragraph_font_family']"
                  label="Paragraph Font Family"
                  onChange={(value) => setFieldValue('typography[\'theme_design/font/paragraph_font_family\']', value)}
                  items={definedGoogleFontFamilies}
                />
                <ColorSelectorGrid
                  value={values.typography['theme_design/font/paragraph_font_color']}
                  name="typography['theme_design/font/paragraph_font_color']"
                  label="Paragraph Text Color"
                  onChange={(color) => setFieldValue('typography[\'theme_design/font/paragraph_font_color\']', color)}
                  InputProps={{
                    value: values.typography['theme_design/font/paragraph_font_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <FontSizeSelectorGrid
                  value={values.typography['theme_design/font/paragraph_font_size']}
                  name="typography['theme_design/font/paragraph_font_size']"
                  label="Paragraph Font Size"
                  onChange={(value) => setFieldValue('typography[\'theme_design/font/paragraph_font_size\']', value)}
                />
                <TextFieldSelectorGrid
                  value={values.typography['theme_design/font/paragraph_line_height']}
                  name="typography['theme_design/font/paragraph_line_height']"
                  label="Paragraph Line Height"
                  items={lineHeightValues}
                  onChange={(value) => setFieldValue('typography[\'theme_design/font/paragraph_line_height\']', value)}
                  InputProps={{
                    value: values.typography['theme_design/font/paragraph_line_height'],
                    shrink: 'true',
                  }}
                />
                <Typography variant="h6" className="ml-3 mb-4">List Styles</Typography>
                <TextFieldSelectorGrid
                  value={values.typography['theme_design/font/list_style_type']}
                  name="typography['theme_design/font/list_style_type']"
                  label="List Style Type"
                  items={listStyleTypes}
                  onChange={(value) => setFieldValue('typography[\'theme_design/font/list_style_type\']', value)}
                  InputProps={{
                    value: values.typography['theme_design/font/list_style_type'],
                    shrink: 'true',
                  }}
                />
              </AccordionDetails>
            </Accordion>
            <Accordion
              elevation={25}
              className={`mt-4 border-radius-4 overflow-visible ${classes.expanded_index}`}
              expanded={expandedIndex === '3'}
              onChange={handleExpansionClick('3')}
            >
              <AccordionSummary
                expandIcon={<Icon>expand_more</Icon>}
              >
                <Typography variant="h5" className="flex items-center text-brand m-2">
                  <Icon className="mr-2">link</Icon>
                  Links
                </Typography>
              </AccordionSummary>
              <AccordionDetails className="flex-column">
                <ColorSelectorGrid
                  value={values.links['theme_design/colors/link_color']}
                  name="links['theme_design/colors/link_color']"
                  label="Link Color"
                  onChange={(color) => setFieldValue('links[\'theme_design/colors/link_color\']', color)}
                  InputProps={{
                    value: values.links['theme_design/colors/link_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.links['theme_design/colors/link_hover_color']}
                  name="links['theme_design/colors/link_hover_color']"
                  label="Link Hover Color"
                  onChange={(color) => setFieldValue('links[\'theme_design/colors/link_hover_color\']', color)}
                  InputProps={{
                    value: values.links['theme_design/colors/link_hover_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.links['theme_design/colors/link_text_decoration']}
                  name="links['theme_design/colors/link_text_decoration']"
                  label="Link Text Decoration"
                  items={textDecorationValues}
                  onChange={(value) => setFieldValue('links[\'theme_design/colors/link_text_decoration\']', value)}
                  InputProps={{
                    value: values.links['theme_design/colors/link_text_decoration'],
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.links['theme_design/colors/link_text_decoration_hover']}
                  name="links['theme_design/colors/link_text_decoration_hover']"
                  label="Link Text Decoration Hover"
                  items={textDecorationValues}
                  onChange={(value) => setFieldValue('links[\'theme_design/colors/link_text_decoration_hover\']', value)}
                  InputProps={{
                    value: values.links['theme_design/colors/link_text_decoration_hover'],
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.links['theme_design/colors/link_font_weight']}
                  name="links['theme_design/colors/link_font_weight']"
                  label="Link Font Weight"
                  items={values.typography['theme_design/font/paragraph_font_family']
                    ? getFontWeightParameters(
                      values.typography['theme_design/font/paragraph_font_family'],
                      stylePalette.typography['theme_design/defined_fonts/google_fonts_string']
                    )
                    : [400]
                  }
                  onChange={(value) => setFieldValue('links[\'theme_design/colors/link_font_weight\']', value)}
                  InputProps={{
                    value: values.links['theme_design/colors/link_font_weight'],
                    shrink: 'true',
                  }}
                />
              </AccordionDetails>
            </Accordion>
            <Accordion
              elevation={25}
              className={`mt-4 border-radius-4 overflow-visible ${classes.expanded_index}`}
              expanded={expandedIndex === '4'}
              onChange={handleExpansionClick('4')}
            >
              <AccordionSummary
                expandIcon={<Icon>expand_more</Icon>}
              >
                <Typography variant="h5" className="flex items-center text-brand m-2">
                  <Icon className="mr-2">flip_to_front</Icon>
                  Buttons
                </Typography>
              </AccordionSummary>
              <AccordionDetails className="flex-column">
                <ColorSelectorGrid
                  value={values.buttons['theme_design/colors/button_bg_color']}
                  name="buttons['theme_design/colors/button_bg_color']"
                  label="Button Background Color"
                  onChange={(color) => setFieldValue('buttons[\'theme_design/colors/button_bg_color\']', color)}
                  InputProps={{
                    value: values.buttons['theme_design/colors/button_bg_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.buttons['theme_design/colors/button_color']}
                  name="buttons['theme_design/colors/button_color']"
                  label="Button Color"
                  onChange={(color) => setFieldValue('buttons[\'theme_design/colors/button_color\']', color)}
                  InputProps={{
                    value: values.buttons['theme_design/colors/button_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.buttons['theme_design/colors/button_hover_bg_color']}
                  name="buttons['theme_design/colors/button_hover_bg_color']"
                  label="Button Background Color Hover"
                  onChange={(color) => setFieldValue('buttons[\'theme_design/colors/button_hover_bg_color\']', color)}
                  InputProps={{
                    value: values.buttons['theme_design/colors/button_hover_bg_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.buttons['theme_design/colors/button_hover_color']}
                  name="buttons['theme_design/colors/button_hover_color']"
                  label="Button Color Hover"
                  onChange={(color) => setFieldValue('buttons[\'theme_design/colors/button_hover_color\']', color)}
                  InputProps={{
                    value: values.buttons['theme_design/colors/button_hover_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.buttons['theme_design/colors/button_font_family']}
                  name="buttons['theme_design/colors/button_font_family']"
                  label="Button Font Family"
                  onChange={(value) => setFieldValue('buttons[\'theme_design/colors/button_font_family\']', value)}
                  items={definedGoogleFontFamilies}
                />
                <FontSizeSelectorGrid
                  value={values.buttons['theme_design/colors/button_font_size']}
                  name="buttons['theme_design/colors/button_font_size']"
                  label="Button Font Size"
                  items={buttonFontSizeValues}
                  onChange={(value) => setFieldValue('buttons[\'theme_design/colors/button_font_size\']', value)}
                />
                <TextFieldSelectorGrid
                  value={values.buttons['theme_design/colors/button_text_transform']}
                  name="buttons['theme_design/colors/button_text_transform']"
                  label="Button Text Transform"
                  items={textTransformValues}
                  onChange={(value) => setFieldValue('buttons[\'theme_design/colors/button_text_transform\']', value)}
                  InputProps={{
                    value: values.buttons['theme_design/colors/button_text_transform'],
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.buttons['theme_design/colors/button_font_weight']}
                  name="buttons['theme_design/colors/button_font_weight']"
                  label="Button Font Weight"
                  items={values.buttons['theme_design/colors/button_font_family']
                    ? getFontWeightParameters(
                      values.buttons['theme_design/colors/button_font_family'],
                      stylePalette.typography['theme_design/defined_fonts/google_fonts_string']
                    )
                    : [400]
                  }
                  onChange={(value) => setFieldValue('buttons[\'theme_design/colors/button_font_weight\']', value)}
                  InputProps={{
                    value: values.buttons['theme_design/colors/button_font_weight'],
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.buttons['theme_design/colors/button_font_style']}
                  name="buttons['theme_design/colors/button_font_style']"
                  label="Button Font Style"
                  items={fontStyleValues}
                  onChange={(value) => setFieldValue('buttons[\'theme_design/colors/button_font_style\']', value)}
                  InputProps={{
                    value: values.buttons['theme_design/colors/button_font_style'],
                    shrink: 'true',
                  }}
                />
                <BorderRadiusSelectorGrid
                  value={values.buttons['theme_design/colors/button_border_radius']}
                  name="buttons['theme_design/colors/button_border_radius']"
                  label="Button Border Radius"
                  setValue={(value) => setFieldValue('buttons[\'theme_design/colors/button_border_radius\']', value)}
                  InputProps={{
                    value: values.buttons['theme_design/colors/button_border_radius'],
                    shrink: 'true',
                  }}
                />
                <SwitchGrid
                  value={values.buttons['theme_design/colors/button_border'] !== 'none'}
                  name="buttons['theme_design/colors/button_border']"
                  label="Add Button Border?"
                  onChange={(value) => {
                    if (value == 1) {
                      setFieldValue('buttons[\'theme_design/colors/button_border\']', '0px solid #fff');
                      setFieldValue('buttons[\'theme_design/colors/button_border_hover\']', '0px solid #fff');
                    } else {
                      setFieldValue('buttons[\'theme_design/colors/button_border\']', 'none');
                      setFieldValue('buttons[\'theme_design/colors/button_border_hover\']', 'none');
                    }
                  }}
                />
                {values.buttons['theme_design/colors/button_border'] !== 'none' &&
                  <>
                    <BorderSelectorGrid
                      value={values.buttons['theme_design/colors/button_border']}
                      name="buttons['theme_design/colors/button_border']"
                      label="Button Border"
                      setValue={(value) => setFieldValue('buttons[\'theme_design/colors/button_border\']', value)}
                      InputProps={{
                        value: values.buttons['theme_design/colors/button_border'],
                        readOnly: true,
                        shrink: 'true',
                      }}
                    />
                    <BorderSelectorGrid
                      value={values.buttons['theme_design/colors/button_border_hover']}
                      name="buttons['theme_design/colors/button_border_hover']"
                      label="Button Border Hover"
                      setValue={(value) => setFieldValue('buttons[\'theme_design/colors/button_border_hover\']', value)}
                      InputProps={{
                        value: values.buttons['theme_design/colors/button_border_hover'],
                        readOnly: true,
                        shrink: 'true',
                      }}
                    />
                  </>
                }
              </AccordionDetails>
            </Accordion>
            <Accordion
              elevation={25}
              className={`mt-4 border-radius-4 overflow-visible ${classes.expanded_index}`}
              expanded={expandedIndex === '5'}
              onChange={handleExpansionClick('5')}
            >
              <AccordionSummary
                expandIcon={<Icon>expand_more</Icon>}
              >
                <Typography variant="h5" className="flex items-center text-brand m-2">
                  <Icon className="mr-2">blur_circular</Icon>
                  Labels
                </Typography>
              </AccordionSummary>
              <AccordionDetails className="flex-column">
                <ColorSelectorGrid
                  value={values.labels['theme_design/colors/label_new_bg_color']}
                  name="labels['theme_design/colors/label_new_bg_color']"
                  label="Label 'New' Background Color"
                  onChange={(color) => setFieldValue('labels[\'theme_design/colors/label_new_bg_color\']', color)}
                  InputProps={{
                    value: values.labels['theme_design/colors/label_new_bg_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.labels['theme_design/colors/label_new_color']}
                  name="labels['theme_design/colors/label_new_color']"
                  label="Label 'New' Color"
                  onChange={(color) => setFieldValue('labels[\'theme_design/colors/label_new_color\']', color)}
                  InputProps={{
                    value: values.labels['theme_design/colors/label_new_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.labels['theme_design/colors/label_sale_bg_color']}
                  name="labels['theme_design/colors/label_sale_bg_color']"
                  label="Label 'Sale' Background Color"
                  onChange={(color) => setFieldValue('labels[\'theme_design/colors/label_sale_bg_color\']', color)}
                  InputProps={{
                    value: values.labels['theme_design/colors/label_sale_bg_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.labels['theme_design/colors/label_sale_color']}
                  name="labels['theme_design/colors/label_sale_color']"
                  label="Label 'Sale' Color"
                  onChange={(color) => setFieldValue('labels[\'theme_design/colors/label_sale_color\']', color)}
                  InputProps={{
                    value: values.labels['theme_design/colors/label_sale_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
              </AccordionDetails>
            </Accordion>
            <Accordion
              elevation={25}
              className={`mt-4 border-radius-4 overflow-visible ${classes.expanded_index}`}
              expanded={expandedIndex === '6'}
              onChange={handleExpansionClick('6')}
            >
              <AccordionSummary
                expandIcon={<Icon>expand_more</Icon>}
              >
                <Typography variant="h5" className="flex items-center text-brand m-2">
                  <Icon className="mr-2">dvr</Icon>
                  Header
                </Typography>
              </AccordionSummary>
              <AccordionDetails className="flex-column">
                <Typography variant="h6" className="ml-3 mb-4">General</Typography>
                <ColorSelectorGrid
                  value={values.header['theme_design/header/header_bg_color']}
                  name="header['theme_design/header/header_bg_color']"
                  label="Header Background Color"
                  onChange={(color) => setFieldValue('header[\'theme_design/header/header_bg_color\']', color)}
                  InputProps={{
                    value: values.header['theme_design/header/header_bg_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.header['theme_design/header/top_border_color']}
                  name="header['theme_design/header/top_border_color']"
                  label="Header Top Border Color"
                  onChange={(color) => setFieldValue('header[\'theme_design/header/top_border_color\']', color)}
                  InputProps={{
                    value: values.header['theme_design/header/top_border_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <FontSizeSelectorGrid
                  value={values.header['theme_design/header_primary/text_font_size']}
                  name="header['theme_design/header_primary/text_font_size']"
                  label="Header Text Font Size"
                  items={buttonFontSizeValues}
                  onChange={(value) => setFieldValue('header[\'theme_design/header_primary/text_font_size\']', value)}
                />
                <ColorSelectorGrid
                  value={values.header['theme_design/header_primary/text_color']}
                  name="header['theme_design/header_primary/text_color']"
                  label="Header Text Color"
                  onChange={(color) => setFieldValue('header[\'theme_design/header_primary/text_color\']', color)}
                  InputProps={{
                    value: values.header['theme_design/header_primary/text_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.header['theme_design/header_primary/text_link_color']}
                  name="header['theme_design/header_primary/text_link_color']"
                  label="Header Link Color"
                  onChange={(color) => setFieldValue('header[\'theme_design/header_primary/text_link_color\']', color)}
                  InputProps={{
                    value: values.header['theme_design/header_primary/text_link_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.header['theme_design/header_primary/text_link_hover_color']}
                  name="header['theme_design/header_primary/text_link_hover_color']"
                  label="Header Link Color Hover"
                  onChange={(color) => setFieldValue('header[\'theme_design/header_primary/text_link_hover_color\']', color)}
                  InputProps={{
                    value: values.header['theme_design/header_primary/text_link_hover_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                {isPremium &&
                  <>
                    <ColorSelectorGrid
                      value={values.header['theme_design/header_primary/button_background']}
                      name="header['theme_design/header_primary/button_background']"
                      label="Header Button Background Color"
                      onChange={(color) => setFieldValue('header[\'theme_design/header_primary/button_background\']', color)}
                      InputProps={{
                        value: values.header['theme_design/header_primary/button_background'],
                        readOnly: true,
                        shrink: 'true',
                      }}
                    />
                    <ColorSelectorGrid
                      value={values.header['theme_design/header_primary/button_color']}
                      name="header['theme_design/header_primary/button_color']"
                      label="Header Button Color"
                      onChange={(color) => setFieldValue('header[\'theme_design/header_primary/button_color\']', color)}
                      InputProps={{
                        value: values.header['theme_design/header_primary/button_color'],
                        readOnly: true,
                        shrink: 'true',
                      }}
                    />
                    <ColorSelectorGrid
                      value={values.header['theme_design/header_primary/button_hover_background']}
                      name="header['theme_design/header_primary/button_hover_background']"
                      label="Header Button Background Color Hover"
                      onChange={(color) => setFieldValue('header[\'theme_design/header_primary/button_hover_background\']', color)}
                      InputProps={{
                        value: values.header['theme_design/header_primary/button_hover_background'],
                        readOnly: true,
                        shrink: 'true',
                      }}
                    />
                    <ColorSelectorGrid
                      value={values.header['theme_design/header_primary/button_hover_color']}
                      name="header['theme_design/header_primary/button_hover_color']"
                      label="Header Button Color Hover"
                      onChange={(color) => setFieldValue('header[\'theme_design/header_primary/button_hover_color\']', color)}
                      InputProps={{
                        value: values.header['theme_design/header_primary/button_hover_color'],
                        readOnly: true,
                        shrink: 'true',
                      }}
                    />
                  </>
                }
              </AccordionDetails>
            </Accordion>
            {isPremiumPlus &&
              <Accordion
                elevation={25}
                className={`mt-4 border-radius-4 overflow-visible ${classes.expanded_index}`}
                expanded={expandedIndex === '18'}
                onChange={handleExpansionClick('18')}
              >
                <AccordionSummary
                  expandIcon={<Icon>expand_more</Icon>}
                >
                  <Typography variant="h5" className="flex items-center text-brand m-2">
                    <Icon className="mr-2">open_in_browser_icon</Icon>
                    Additional Header
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className="flex-column">
                  <ColorSelectorGrid
                    value={values.additional_header['theme_design/premium_plus_homepage/additional_header_bg']}
                    name="additional_header['theme_design/premium_plus_homepage/additional_header_bg']"
                    label="Header Background Color"
                    onChange={(color) => setFieldValue('additional_header[\'theme_design/premium_plus_homepage/additional_header_bg\']', color)}
                    InputProps={{
                      value: values.additional_header['theme_design/premium_plus_homepage/additional_header_bg'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.additional_header['theme_design/premium_plus_homepage/additional_header_text_color']}
                    name="additional_header['theme_design/premium_plus_homepage/additional_header_text_color']"
                    label="Header Text Color"
                    onChange={(color) => setFieldValue('additional_header[\'theme_design/premium_plus_homepage/additional_header_text_color\']', color)}
                    InputProps={{
                      value: values.additional_header['theme_design/premium_plus_homepage/additional_header_text_color'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <NumberFormatGrid
                    value={values.additional_header['theme_design/premium_plus_homepage/additional_header_text_font_size']}
                    suffix="px"
                    name={'additional_header[\'theme_design/premium_plus_homepage/additional_header_text_font_size\']'}
                    label="Header Text Font Size"
                    onChange={(value) => setFieldValue('additional_header[\'theme_design/premium_plus_homepage/additional_header_text_font_size\']', value)}
                    InputProps={{
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.additional_header['theme_design/premium_plus_homepage/additional_header_icon_color']}
                    name="additional_header['theme_design/premium_plus_homepage/additional_header_icon_color']"
                    label="Header Icon Color"
                    onChange={(color) => setFieldValue('additional_header[\'theme_design/premium_plus_homepage/additional_header_icon_color\']', color)}
                    InputProps={{
                      value: values.additional_header['theme_design/premium_plus_homepage/additional_header_icon_color'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.additional_header['theme_design/premium_plus_homepage/additional_header_icon_color_hover']}
                    name="additional_header['theme_design/premium_plus_homepage/additional_header_icon_color_hover']"
                    label="Header Icon Color Hover"
                    onChange={(color) => setFieldValue('additional_header[\'theme_design/premium_plus_homepage/additional_header_icon_color_hover\']', color)}
                    InputProps={{
                      value: values.additional_header['theme_design/premium_plus_homepage/additional_header_icon_color_hover'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.additional_header['theme_design/premium_plus_homepage/additional_header_text_color_hover']}
                    name="additional_header['theme_design/premium_plus_homepage/additional_header_text_color_hover']"
                    label="Header Text Color Hover"
                    onChange={(color) => setFieldValue('additional_header[\'theme_design/premium_plus_homepage/additional_header_text_color_hover\']', color)}
                    InputProps={{
                      value: values.additional_header['theme_design/premium_plus_homepage/additional_header_text_color_hover'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                </AccordionDetails>
              </Accordion>
            }
            <Accordion
              elevation={25}
              className={`mt-4 border-radius-4 overflow-visible ${classes.expanded_index}`}
              expanded={expandedIndex === '7'}
              onChange={handleExpansionClick('7')}
            >
              <AccordionSummary
                expandIcon={<Icon>expand_more</Icon>}
              >
                <Typography variant="h5" className="flex items-center text-brand m-2">
                  <Icon className="mr-2">mobile_friendly</Icon>
                  Mobile
                </Typography>
              </AccordionSummary>
              <AccordionDetails className="flex-column">
                <ColorSelectorGrid
                  value={values.mobile['theme_design/m_header/tab_item_bg_color']}
                  name="mobile['theme_design/m_header/tab_item_bg_color']"
                  label="Menu Item Background Color"
                  onChange={(color) => setFieldValue('mobile[\'theme_design/m_header/tab_item_bg_color\']', color)}
                  InputProps={{
                    value: values.mobile['theme_design/m_header/tab_item_bg_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.mobile['theme_design/m_header/tab_item_color']}
                  name="mobile['theme_design/m_header/tab_item_color']"
                  label="Menu Item Text Color"
                  onChange={(color) => setFieldValue('mobile[\'theme_design/m_header/tab_item_color\']', color)}
                  InputProps={{
                    value: values.mobile['theme_design/m_header/tab_item_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.mobile['theme_design/m_header/counter_bg_active_color']}
                  name="mobile['theme_design/m_header/counter_bg_active_color']"
                  label="Counter Color"
                  onChange={(color) => setFieldValue('mobile[\'theme_design/m_header/counter_bg_active_color\']', color)}
                  InputProps={{
                    value: values.mobile['theme_design/m_header/counter_bg_active_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.mobile['theme_design/m_header/counter_bg_color']}
                  name="mobile['theme_design/m_header/counter_bg_color']"
                  label="Counter Background Color"
                  onChange={(color) => setFieldValue('mobile[\'theme_design/m_header/counter_bg_color\']', color)}
                  InputProps={{
                    value: values.mobile['theme_design/m_header/counter_bg_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.mobile['theme_design/m_header/sub_menu_bg']}
                  name="mobile['theme_design/m_header/sub_menu_bg']"
                  label="Sub Menu Background Color"
                  onChange={(color) => setFieldValue('mobile[\'theme_design/m_header/sub_menu_bg\']', color)}
                  InputProps={{
                    value: values.mobile['theme_design/m_header/sub_menu_bg'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.mobile['theme_design/m_header/sub_menu_color']}
                  name="mobile['theme_design/m_header/sub_menu_color']"
                  label="Sub Menu Text Color"
                  onChange={(color) => setFieldValue('mobile[\'theme_design/m_header/sub_menu_color\']', color)}
                  InputProps={{
                    value: values.mobile['theme_design/m_header/sub_menu_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.mobile['theme_design/m_header/sub_menu_font_family']}
                  name="mobile['theme_design/m_header/sub_menu_font_family']"
                  label="Sub Menu Font Family"
                  onChange={(value) => setFieldValue('mobile[\'theme_design/m_header/sub_menu_font_family\']', value)}
                  items={definedGoogleFontFamilies}
                />
                <FontSizeSelectorGrid
                  value={values.mobile['theme_design/m_header/sub_menu_font_size']}
                  name="mobile['theme_design/m_header/sub_menu_font_size']"
                  label="Sub Menu Font Size"
                  onChange={(value) => setFieldValue('mobile[\'theme_design/m_header/sub_menu_font_size\']', value)}
                />
                <TextFieldSelectorGrid
                  value={values.mobile['theme_design/m_header/sub_menu_font_weight']}
                  name="mobile['theme_design/m_header/sub_menu_font_weight']"
                  label="Sub Menu Font Weight"
                  items={values.mobile['theme_design/m_header/sub_menu_font_family']
                    ? getFontWeightParameters(
                      values.mobile['theme_design/m_header/sub_menu_font_family'],
                      stylePalette.typography['theme_design/defined_fonts/google_fonts_string']
                    )
                    : [400]
                  }
                  onChange={(value) => setFieldValue('mobile[\'theme_design/m_header/sub_menu_font_weight\']', value)}
                  InputProps={{
                    value: values.mobile['theme_design/m_header/sub_menu_font_weight'],
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.mobile['theme_design/m_header/sub_menu_text_transform']}
                  name="mobile['theme_design/m_header/sub_menu_text_transform']"
                  label="Sub Menu Text Transform"
                  items={textTransformValues}
                  onChange={(value) => setFieldValue('mobile[\'theme_design/m_header/sub_menu_text_transform\']', value)}
                  InputProps={{
                    value: values.mobile['theme_design/m_header/sub_menu_text_transform'],
                    shrink: 'true',
                  }}
                />
              </AccordionDetails>
            </Accordion>
            <Accordion
              elevation={25}
              className={`mt-4 border-radius-4 overflow-visible ${classes.expanded_index}`}
              expanded={expandedIndex === '8'}
              onChange={handleExpansionClick('8')}
            >
              <AccordionSummary
                expandIcon={<Icon>expand_more</Icon>}
              >
                <Typography variant="h5" className="flex items-center text-brand m-2">
                  <Icon className="mr-2">menu</Icon>
                  Menu
                </Typography>
              </AccordionSummary>
              <AccordionDetails className="flex-column">
                <Typography variant="h6" className="ml-3 mb-4">Main Menu</Typography>
                <ColorSelectorGrid
                  value={values.menu['theme_design/nav/outer_menu_bg_color']}
                  name="menu['theme_design/nav/outer_menu_bg_color']"
                  label="Menu Background Color"
                  onChange={(color) => setFieldValue('menu[\'theme_design/nav/outer_menu_bg_color\']', color)}
                  InputProps={{
                    value: values.menu['theme_design/nav/outer_menu_bg_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.menu['theme_design/nav/color']}
                  name="menu['theme_design/nav/color']"
                  label="Menu Item Text Color"
                  onChange={(color) => setFieldValue('menu[\'theme_design/nav/color\']', color)}
                  InputProps={{
                    value: values.menu['theme_design/nav/color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.menu['theme_design/nav/hover_bg_color']}
                  name="menu['theme_design/nav/hover_bg_color']"
                  label="Menu Item Background Color Hover"
                  onChange={(color) => setFieldValue('menu[\'theme_design/nav/hover_bg_color\']', color)}
                  InputProps={{
                    value: values.menu['theme_design/nav/hover_bg_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.menu['theme_design/nav/hover_color']}
                  name="menu['theme_design/nav/hover_color']"
                  label="Menu Item Text Color Hover"
                  onChange={(color) => setFieldValue('menu[\'theme_design/nav/hover_color\']', color)}
                  InputProps={{
                    value: values.menu['theme_design/nav/hover_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.menu['theme_design/nav/font_family']}
                  name="menu['theme_design/nav/font_family']"
                  label="Menu Item Font Family"
                  onChange={(value) => setFieldValue('menu[\'theme_design/nav/font_family\']', value)}
                  items={definedGoogleFontFamilies}
                />
                <FontSizeSelectorGrid
                  value={values.menu['theme_design/nav/font_size']}
                  name="menu['theme_design/nav/font_size']"
                  label="Menu Item Font Size"
                  items={buttonFontSizeValues}
                  onChange={(value) => setFieldValue('menu[\'theme_design/nav/font_size\']', value)}
                />
                <TextFieldSelectorGrid
                  value={values.menu['theme_design/nav/text_transform']}
                  name="menu['theme_design/nav/text_transform']"
                  label="Menu Item Text Transform"
                  items={textTransformValues}
                  onChange={(value) => setFieldValue('menu[\'theme_design/nav/text_transform\']', value)}
                  InputProps={{
                    value: values.menu['theme_design/nav/text_transform'],
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.menu['theme_design/nav/font_weight']}
                  name="menu['theme_design/nav/font_weight']"
                  label="Menu Item Font Weight"
                  items={values.menu['theme_design/nav/font_family']
                    ? getFontWeightParameters(
                      values.menu['theme_design/nav/font_family'],
                      stylePalette.typography['theme_design/defined_fonts/google_fonts_string']
                    )
                    : [400]
                  }
                  onChange={(value) => setFieldValue('menu[\'theme_design/nav/font_weight\']', value)}
                  InputProps={{
                    value: values.menu['theme_design/nav/font_weight'],
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.menu['theme_design/nav/text_decoration']}
                  name="menu['theme_design/nav/text_decoration']"
                  label="Menu Item Text Decoration"
                  items={textDecorationValues}
                  onChange={(value) => setFieldValue('menu[\'theme_design/nav/text_decoration\']', value)}
                  InputProps={{
                    value: values.menu['theme_design/nav/text_decoration'],
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.menu['theme_design/nav/text_decoration_hover']}
                  name="menu['theme_design/nav/text_decoration_hover']"
                  label="Menu Item Text Decoration Hover"
                  items={textDecorationValues}
                  onChange={(value) => setFieldValue('menu[\'theme_design/nav/text_decoration_hover\']', value)}
                  InputProps={{
                    value: values.menu['theme_design/nav/text_decoration_hover'],
                    shrink: 'true',
                  }}
                />
                <TextFieldGrid
                  value={values.menu['theme_design/nav/letter_spacing']}
                  name="menu['theme_design/nav/letter_spacing']"
                  label="Menu Item Letter Spacing"
                  items={textDecorationValues}
                  onChange={(value) => setFieldValue('menu[\'theme_design/nav/letter_spacing\']', value)}
                  InputProps={{
                    value: values.menu['theme_design/nav/letter_spacing'],
                    shrink: 'true',
                  }}
                />
                <SwitchGrid
                  value={values.menu['theme_design/nav/is_menu_item_underline_effect']}
                  name="menu['theme_design/nav/is_menu_item_underline_effect']"
                  label="Additional underline effect on hover/active"
                  onChange={(value) => setFieldValue('menu[\'theme_design/nav/is_menu_item_underline_effect\']', value)}
                />
                {values.menu['theme_design/nav/is_menu_item_underline_effect'] == '1' &&
                  <>
                    <ColorSelectorGrid
                      value={values.menu['theme_design/nav/underline_color']}
                      name="menu['theme_design/nav/underline_color']"
                      label="Line Color"
                      onChange={(color) => setFieldValue('menu[\'theme_design/nav/underline_color\']', color)}
                      InputProps={{
                        value: values.menu['theme_design/nav/underline_color'],
                        readOnly: true,
                        shrink: 'true',
                      }}
                    />
                    <TextFieldGrid
                      value={values.menu['theme_design/nav/underline_height']}
                      name="menu['theme_design/nav/underline_height']"
                      label="Line Height"
                      items={textDecorationValues}
                      onChange={(value) => setFieldValue('menu[\'theme_design/nav/underline_height\']', value)}
                      InputProps={{
                        value: values.menu['theme_design/nav/underline_height'],
                        shrink: 'true',
                      }}
                    />
                  </>
                }
                <Typography variant="h6" className="ml-3 mb-4">Sub Menu</Typography>
                <ColorSelectorGrid
                  value={values.menu['theme_design/nav/dropdown_bg_color']}
                  name="menu['theme_design/nav/dropdown_bg_color']"
                  label="Drop-down Background Color"
                  onChange={(color) => setFieldValue('menu[\'theme_design/nav/dropdown_bg_color\']', color)}
                  InputProps={{
                    value: values.menu['theme_design/nav/dropdown_bg_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.menu['theme_design/nav/dropdown_link_color']}
                  name="menu['theme_design/nav/dropdown_link_color']"
                  label="Drop-down Text Color"
                  onChange={(color) => setFieldValue('menu[\'theme_design/nav/dropdown_link_color\']', color)}
                  InputProps={{
                    value: values.menu['theme_design/nav/dropdown_link_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.menu['theme_design/nav/dropdown_item_hover_bg_color']}
                  name="menu['theme_design/nav/dropdown_item_hover_bg_color']"
                  label="Drop-down Background Color Hover"
                  onChange={(color) => setFieldValue('menu[\'theme_design/nav/dropdown_item_hover_bg_color\']', color)}
                  InputProps={{
                    value: values.menu['theme_design/nav/dropdown_item_hover_bg_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.menu['theme_design/nav/dropdown_link_hover_color']}
                  name="menu['theme_design/nav/dropdown_link_hover_color']"
                  label="Drop-down Text Color Hover"
                  onChange={(color) => setFieldValue('menu[\'theme_design/nav/dropdown_link_hover_color\']', color)}
                  InputProps={{
                    value: values.menu['theme_design/nav/dropdown_link_hover_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.menu['theme_design/nav/dropdown_font_family']}
                  name="menu['theme_design/nav/dropdown_font_family']"
                  label="Dropdown Text Font Family"
                  onChange={(value) => setFieldValue('menu[\'theme_design/nav/dropdown_font_family\']', value)}
                  items={definedGoogleFontFamilies}
                />
                <FontSizeSelectorGrid
                  value={values.menu['theme_design/nav/dropdown_font_size']}
                  name="menu['theme_design/nav/dropdown_font_size']"
                  label="Dropdown Text Font Size"
                  items={buttonFontSizeValues}
                  onChange={(value) => setFieldValue('menu[\'theme_design/nav/dropdown_font_size\']', value)}
                />
                <TextFieldSelectorGrid
                  value={values.menu['theme_design/nav/dropdown_text_transform']}
                  name="menu['theme_design/nav/dropdown_text_transform']"
                  label="Dropdown Text Transform"
                  items={textTransformValues}
                  onChange={(value) => setFieldValue('menu[\'theme_design/nav/dropdown_text_transform\']', value)}
                  InputProps={{
                    value: values.menu['theme_design/nav/dropdown_text_transform'],
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.menu['theme_design/nav/dropdown_font_weight']}
                  name="menu['theme_design/nav/dropdown_font_weight']"
                  label="Dropdown Text Font Weight"
                  items={values.menu['theme_design/nav/dropdown_font_family']
                    ? getFontWeightParameters(
                      values.menu['theme_design/nav/dropdown_font_family'],
                      stylePalette.typography['theme_design/defined_fonts/google_fonts_string']
                    )
                    : [400]
                  }
                  onChange={(value) => setFieldValue('menu[\'theme_design/nav/dropdown_font_weight\']', value)}
                  InputProps={{
                    value: values.menu['theme_design/nav/dropdown_font_weight'],
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.menu['theme_design/nav/dropdown_text_decoration']}
                  name="menu['theme_design/nav/dropdown_text_decoration']"
                  label="Dropdown Text Decoration"
                  items={textDecorationValues}
                  onChange={(value) => setFieldValue('menu[\'theme_design/nav/dropdown_text_decoration\']', value)}
                  InputProps={{
                    value: values.menu['theme_design/nav/dropdown_text_decoration'],
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.menu['theme_design/nav/dropdown_text_decoration_hover']}
                  name="menu['theme_design/nav/dropdown_text_decoration_hover']"
                  label="Dropdown Text Decoration Hover"
                  items={textDecorationValues}
                  onChange={(value) => setFieldValue('menu[\'theme_design/nav/dropdown_text_decoration_hover\']', value)}
                  InputProps={{
                    value: values.menu['theme_design/nav/dropdown_text_decoration_hover'],
                    shrink: 'true',
                  }}
                />
                <SwitchGrid
                  value={values.menu['theme_design/nav/dropdown_item_is_border_bottom']}
                  name="menu['theme_design/nav/dropdown_item_is_border_bottom']"
                  label="Is Dropdown Item Border Bottom"
                  onChange={(value) => {
                    setFieldValue('menu[\'theme_design/nav/dropdown_item_is_border_bottom\']', value);

                    if (value == 1) {
                      setFieldValue('menu[\'theme_design/nav/dropdown_item_border_bottom\']', '0px solid #ffffff');
                    }
                  }}
                />
                {values.menu['theme_design/nav/dropdown_item_is_border_bottom'] == '1' &&
                  <>
                    <BorderSelectorGrid
                      value={values.menu['theme_design/nav/dropdown_item_border_bottom']}
                      name="menu['theme_design/nav/dropdown_item_border_bottom']"
                      label="Border Bottom"
                      items={textDecorationValues}
                      setValue={(value) => setFieldValue('menu[\'theme_design/nav/dropdown_item_border_bottom\']', value)}
                      InputProps={{
                        value: values.menu['theme_design/nav/dropdown_item_border_bottom'],
                        shrink: 'true',
                      }}
                    />
                  </>
                }
              </AccordionDetails>
            </Accordion>
            <Accordion
              elevation={25}
              className={`mt-4 border-radius-4 overflow-visible ${classes.expanded_index}`}
              expanded={expandedIndex === '9'}
              onChange={handleExpansionClick('9')}
            >
              <AccordionSummary
                expandIcon={<Icon>expand_more</Icon>}
              >
                <Typography variant="h5" className="flex items-center text-brand m-2">
                  <Icon className="mr-2">system_update</Icon>
                  Footer
                </Typography>
              </AccordionSummary>
              <AccordionDetails className="flex-column">
                <Typography variant="h6" className="ml-3 mb-4">General</Typography>
                <SwitchGrid
                  value={values.footer['theme_design/footer/border_top'] !== 'none'}
                  name="footer['theme_design/footer/border_top']"
                  label="Add Footer Top Border?"
                  onChange={(value) => {
                    if (value == 1) {
                      setFieldValue('footer[\'theme_design/footer/border_top\']', '0px solid #fff');
                    } else {
                      setFieldValue('footer[\'theme_design/footer/border_top\']', 'none');
                    }
                  }}
                />
                {values.footer['theme_design/footer/border_top'] !== 'none' &&
                  <BorderSelectorGrid
                    value={values.footer['theme_design/footer/border_top']}
                    name="footer['theme_design/footer/border_top']"
                    label="Footer Top Border"
                    items={textDecorationValues}
                    setValue={(value) => setFieldValue('footer[\'theme_design/footer/border_top\']', value)}
                    InputProps={{
                      value: values.footer['theme_design/footer/border_top'],
                      shrink: 'true',
                    }}
                  />
                }
                <ColorSelectorGrid
                  value={values.footer['theme_design/footer/bg_color']}
                  name="footer['theme_design/footer/bg_color']"
                  label="Footer Background Color"
                  onChange={(color) => setFieldValue('footer[\'theme_design/footer/bg_color\']', color)}
                  InputProps={{
                    value: values.footer['theme_design/footer/bg_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.footer['theme_design/footer/color']}
                  name="footer['theme_design/footer/color']"
                  label="Footer Text Color"
                  onChange={(color) => setFieldValue('footer[\'theme_design/footer/color\']', color)}
                  InputProps={{
                    value: values.footer['theme_design/footer/color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.footer['theme_design/footer/link_color']}
                  name="footer['theme_design/footer/link_color']"
                  label="Footer Link Color"
                  onChange={(color) => setFieldValue('footer[\'theme_design/footer/link_color\']', color)}
                  InputProps={{
                    value: values.footer['theme_design/footer/link_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.footer['theme_design/footer/link_hover_color']}
                  name="footer['theme_design/footer/link_hover_color']"
                  label="Footer Link Color Hover"
                  onChange={(color) => setFieldValue('footer[\'theme_design/footer/link_hover_color\']', color)}
                  InputProps={{
                    value: values.footer['theme_design/footer/link_hover_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.footer['theme_design/footer/button_bg_color']}
                  name="footer['theme_design/footer/button_bg_color']"
                  label="Footer Button Background Color"
                  onChange={(color) => setFieldValue('footer[\'theme_design/footer/button_bg_color\']', color)}
                  InputProps={{
                    value: values.footer['theme_design/footer/button_bg_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.footer['theme_design/footer/button_color']}
                  name="footer['theme_design/footer/button_color']"
                  label="Footer Button Text Color"
                  onChange={(color) => setFieldValue('footer[\'theme_design/footer/button_color\']', color)}
                  InputProps={{
                    value: values.footer['theme_design/footer/button_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.footer['theme_design/footer/button_hover_bg_color']}
                  name="footer['theme_design/footer/button_hover_bg_color']"
                  label="Footer Button Background Color Hover"
                  onChange={(color) => setFieldValue('footer[\'theme_design/footer/button_hover_bg_color\']', color)}
                  InputProps={{
                    value: values.footer['theme_design/footer/button_hover_bg_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.footer['theme_design/footer/button_hover_color']}
                  name="footer['theme_design/footer/button_hover_color']"
                  label="Footer Button Text Color Hover"
                  onChange={(color) => setFieldValue('footer[\'theme_design/footer/button_hover_color\']', color)}
                  InputProps={{
                    value: values.footer['theme_design/footer/button_hover_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                {isRegular &&
                  <>
                    <Typography variant="h6" className="ml-3 mb-4">Footer Top (Tagline)</Typography>
                    <ColorSelectorGrid
                      value={values.footer['theme_design/footer_tagline/bg_color']}
                      name="footer['theme_design/footer_tagline/bg_color']"
                      label="Tagline Background Color"
                      onChange={(color) => setFieldValue('footer[\'theme_design/footer_tagline/bg_color\']', color)}
                      InputProps={{
                        value: values.footer['theme_design/footer_tagline/bg_color'],
                        readOnly: true,
                        shrink: 'true',
                      }}
                    />
                    <ColorSelectorGrid
                      value={values.footer['theme_design/footer_tagline/color']}
                      name="footer['theme_design/footer_tagline/color']"
                      label="Tagline Text Color"
                      onChange={(color) => setFieldValue('footer[\'theme_design/footer_tagline/color\']', color)}
                      InputProps={{
                        value: values.footer['theme_design/footer_tagline/color'],
                        readOnly: true,
                        shrink: 'true',
                      }}
                    />
                    <NumberFormatGrid
                      value={values.footer['theme_design/footer_tagline/font_size']}
                      suffix="px"
                      name="footer['theme_design/footer_tagline/font_size']"
                      label="Tagline Font Size"
                      onChange={(value) => setFieldValue('footer[\'theme_design/footer_tagline/font_size\']', value)}
                      InputProps={{
                        shrink: 'true',
                      }}
                    />
                    <TextFieldSelectorGrid
                      value={values.footer['theme_design/footer_tagline/font_weight']}
                      name="footer['theme_design/footer_tagline/font_weight']"
                      label="Tagline Font Weight"
                      items={values.typography['theme_design/font/heading3_font_family']
                        ? getFontWeightParameters(
                          values.typography['theme_design/font/heading3_font_family'],
                          stylePalette.typography['theme_design/defined_fonts/google_fonts_string']
                        )
                        : [400]
                      }
                      onChange={(value) => setFieldValue('footer[\'theme_design/footer_tagline/font_weight\']', value)}
                      InputProps={{
                        value: values.footer['theme_design/footer_tagline/font_weight'],
                        shrink: 'true',
                      }}
                    />
                  </>
                }
                {isPremiumPlus &&
                <>
                  <TextFieldSelectorGrid
                    value={values.footer['theme_design/premium_plus_footer/text_transform']}
                    name="footer['theme_design/premium_plus_footer/text_transform']"
                    label="Footer Text Transform"
                    items={textTransformValues}
                    onChange={(value) => setFieldValue('footer[\'theme_design/premium_plus_footer/text_transform\']', value)}
                    InputProps={{
                      value: values.footer['theme_design/premium_plus_footer/text_transform'],
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.footer['theme_design/premium_plus_footer/newsletter_bg_color']}
                    name="footer['theme_design/premium_plus_footer/newsletter_bg_color']"
                    label="Newsletter Input Background Color"
                    onChange={(color) => setFieldValue('footer[\'theme_design/premium_plus_footer/newsletter_bg_color\']', color)}
                    InputProps={{
                      value: values.footer['theme_design/premium_plus_footer/newsletter_bg_color'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <FontSizeSelectorGrid
                    value={values.footer['theme_design/premium_plus_footer/font_size']}
                    name="footer['theme_design/premium_plus_footer/font_size']"
                    label="Font Size"
                    onChange={(value) => setFieldValue('footer[\'theme_design/premium_plus_footer/font_size\']', value)}
                  />
                  <TextFieldSelectorGrid
                    value={values.footer['theme_design/premium_plus_footer/font_weight']}
                    name="footer['theme_design/premium_plus_footer/font_weight']"
                    label="Font Weight"
                    items={values.typography['theme_design/font/body_font_family']
                      ? getFontWeightParameters(
                        values.typography['theme_design/font/body_font_family'],
                        stylePalette.typography['theme_design/defined_fonts/google_fonts_string']
                      )
                      : [400]
                    }
                    onChange={(value) => setFieldValue('footer[\'theme_design/premium_plus_footer/font_weight\']', value)}
                    InputProps={{
                      value: values.footer['theme_design/premium_plus_footer/font_weight'],
                      shrink: 'true',
                    }}
                  />
                </>
                }
                <Typography variant="h6" className="ml-3 mb-4">Footer Bottom (Copyright Section)</Typography>
                <ColorSelectorGrid
                  value={values.footer['theme_design/footer_bottom/bg_color']}
                  name="footer['theme_design/footer_bottom/bg_color']"
                  label="Footer Bottom Background Color"
                  onChange={(color) => setFieldValue('footer[\'theme_design/footer_bottom/bg_color\']', color)}
                  InputProps={{
                    value: values.footer['theme_design/footer_bottom/bg_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.footer['theme_design/footer_bottom/color']}
                  name="footer['theme_design/footer_bottom/color']"
                  label="Footer Bottom Text Color"
                  onChange={(color) => setFieldValue('footer[\'theme_design/footer_bottom/color\']', color)}
                  InputProps={{
                    value: values.footer['theme_design/footer_bottom/color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.footer['theme_design/footer_bottom/link_color']}
                  name="footer['theme_design/footer_bottom/link_color']"
                  label="Footer Bottom Link Color"
                  onChange={(color) => setFieldValue('footer[\'theme_design/footer_bottom/link_color\']', color)}
                  InputProps={{
                    value: values.footer['theme_design/footer_bottom/link_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.footer['theme_design/footer_bottom/link_hover_color']}
                  name="footer['theme_design/footer_bottom/link_hover_color']"
                  label="Footer Bottom Link Color Hover"
                  onChange={(color) => setFieldValue('footer[\'theme_design/footer_bottom/link_hover_color\']', color)}
                  InputProps={{
                    value: values.footer['theme_design/footer_bottom/link_hover_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
              </AccordionDetails>
            </Accordion>
            <Accordion
              elevation={25}
              className={`mt-4 border-radius-4 overflow-visible ${classes.expanded_index}`}
              expanded={expandedIndex === '10'}
              onChange={handleExpansionClick('10')}
            >
              <AccordionSummary
                expandIcon={<Icon>expand_more</Icon>}
              >
                <Typography variant="h5" className="flex items-center text-brand m-2">
                  <Icon className="mr-2">category</Icon>
                  Category Page
                </Typography>
              </AccordionSummary>
              <AccordionDetails className="flex-column">
                <Typography variant="h6" className="ml-3 mb-4">Product Price</Typography>
                <ColorSelectorGrid
                  value={values.category_page['theme_design/colors/price_color']}
                  name="category_page['theme_design/colors/price_color']"
                  label="Product Price Color"
                  onChange={(color) => setFieldValue('category_page[\'theme_design/colors/price_color\']', color)}
                  InputProps={{
                    value: values.category_page['theme_design/colors/price_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <FontSizeSelectorGrid
                  value={values.category_page['theme_design/colors/price_font_size']}
                  name="category_page['theme_design/colors/price_font_size']"
                  label="Product Price Font Size"
                  onChange={(value) => setFieldValue('category_page[\'theme_design/colors/price_font_size\']', value)}
                />
                <TextFieldSelectorGrid
                  value={values.category_page['theme_design/colors/price_font_weight']}
                  name="category_page['theme_design/colors/price_font_weight']"
                  label="Product Price Font Weight"
                  items={values.typography['theme_design/font/body_font_family']
                    ? getFontWeightParameters(
                      values.typography['theme_design/font/body_font_family'],
                      stylePalette.typography['theme_design/defined_fonts/google_fonts_string']
                    )
                    : [400]
                  }
                  onChange={(value) => setFieldValue('category_page[\'theme_design/colors/price_font_weight\']', value)}
                  InputProps={{
                    value: values.category_page['theme_design/colors/price_font_weight'],
                    shrink: 'true',
                  }}
                />
                <Typography variant="h6" className="ml-3 mb-4">Product Name</Typography>
                <ColorSelectorGrid
                  value={values.category_page['theme_design/colors/product_name_color']}
                  name="category_page['theme_design/colors/product_name_color']"
                  label="Product Name Color"
                  onChange={(color) => setFieldValue('category_page[\'theme_design/colors/product_name_color\']', color)}
                  InputProps={{
                    value: values.category_page['theme_design/colors/product_name_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <FontSizeSelectorGrid
                  value={values.category_page['theme_design/colors/product_name_font_size']}
                  name="category_page['theme_design/colors/product_name_font_size']"
                  label="Product Name Font Size"
                  onChange={(value) => setFieldValue('category_page[\'theme_design/colors/product_name_font_size\']', value)}
                />
                <TextFieldSelectorGrid
                  value={values.category_page['theme_design/colors/product_name_font_weight']}
                  name="category_page['theme_design/colors/product_name_font_weight']"
                  label="Product Name Font Weight"
                  items={values.typography['theme_design/font/body_font_family']
                    ? getFontWeightParameters(
                      values.typography['theme_design/font/body_font_family'],
                      stylePalette.typography['theme_design/defined_fonts/google_fonts_string']
                    )
                    : [400]
                  }
                  onChange={(value) => setFieldValue('category_page[\'theme_design/colors/product_name_font_weight\']', value)}
                  InputProps={{
                    value: values.category_page['theme_design/colors/product_name_font_weight'],
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.category_page['theme_design/colors/product_name_text_transform']}
                  name="category_page['theme_design/colors/product_name_text_transform']"
                  label="Product Name Text Transform"
                  items={textTransformValues}
                  onChange={(value) => setFieldValue('category_page[\'theme_design/colors/product_name_text_transform\']', value)}
                  InputProps={{
                    value: values.category_page['theme_design/colors/product_name_text_transform'],
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.category_page['theme_design/colors/product_name_text_align']}
                  name="category_page['theme_design/colors/product_name_text_align']"
                  label="Product Name Text Align"
                  items={textAlignValues}
                  onChange={(value) => setFieldValue('category_page[\'theme_design/colors/product_name_text_align\']', value)}
                  InputProps={{
                    value: values.category_page['theme_design/colors/product_name_text_align'],
                    shrink: 'true',
                  }}
                />
                <Typography variant="h6" className="ml-3 mb-4">Star Rating</Typography>
                <ColorSelectorGrid
                  value={values.category_page['theme_design/colors/rating_active_color']}
                  name="category_page['theme_design/colors/rating_active_color']"
                  label="Star Rating Color"
                  onChange={(color) => setFieldValue('category_page[\'theme_design/colors/rating_active_color\']', color)}
                  InputProps={{
                    value: values.category_page['theme_design/colors/rating_active_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <Typography variant="h6" className="ml-3 mb-4">Category Promo</Typography>
                <ColorSelectorGrid
                  value={values.category_page['theme_design/category/category_promo_bg_color']}
                  name="category_page['theme_design/category/category_promo_bg_color']"
                  label="Promo Background Color"
                  onChange={(color) => setFieldValue('category_page[\'theme_design/category/category_promo_bg_color\']', color)}
                  InputProps={{
                    value: values.category_page['theme_design/category/category_promo_bg_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.category_page['theme_design/category/category_promo_color']}
                  name="category_page['theme_design/category/category_promo_color']"
                  label="Promo Text Color"
                  onChange={(color) => setFieldValue('category_page[\'theme_design/category/category_promo_color\']', color)}
                  InputProps={{
                    value: values.category_page['theme_design/category/category_promo_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.category_page['theme_design/category/category_promo_font_family']}
                  name="category_page['theme_design/category/category_promo_font_family']"
                  label="Promo Text Font Family"
                  onChange={(value) => setFieldValue('category_page[\'theme_design/category/category_promo_font_family\']', value)}
                  items={definedGoogleFontFamilies}
                />
                <FontSizeSelectorGrid
                  value={values.category_page['theme_design/category/category_promo_font_size']}
                  name="category_page['theme_design/category/category_promo_font_size']"
                  label="Promo Text Font Size"
                  onChange={(value) => setFieldValue('category_page[\'theme_design/category/category_promo_font_size\']', value)}
                />
                <TextFieldSelectorGrid
                  value={values.category_page['theme_design/category/category_promo_font_weight']}
                  name="category_page['theme_design/category/category_promo_font_weight']"
                  label="Promo Text Font Weight"
                  items={values.category_page['theme_design/category/category_promo_font_family']
                    ? getFontWeightParameters(
                      values.category_page['theme_design/category/category_promo_font_family'],
                      stylePalette.typography['theme_design/defined_fonts/google_fonts_string']
                    )
                    : [400]
                  }
                  onChange={(value) => setFieldValue('category_page[\'theme_design/category/category_promo_font_weight\']', value)}
                  InputProps={{
                    value: values.category_page['theme_design/category/category_promo_font_weight'],
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.category_page['theme_design/category/category_promo_line_height']}
                  name="category_page['theme_design/category/category_promo_line_height']"
                  label="Promo Text Line Height"
                  items={lineHeightValues}
                  onChange={(value) => setFieldValue('category_page[\'theme_design/category/category_promo_line_height\']', value)}
                  InputProps={{
                    value: values.category_page['theme_design/category/category_promo_line_height'],
                    shrink: 'true',
                  }}
                />
              </AccordionDetails>
            </Accordion>
            <Accordion
              elevation={25}
              className={`mt-4 border-radius-4 overflow-visible ${classes.expanded_index}`}
              expanded={expandedIndex === '11'}
              onChange={handleExpansionClick('11')}
            >
              <AccordionSummary
                expandIcon={<Icon>expand_more</Icon>}
              >
                <Typography variant="h5" className="flex items-center text-brand m-2">
                  <Icon className="mr-2">pages</Icon>
                  Product Page
                </Typography>
              </AccordionSummary>
              <AccordionDetails className="flex-column">
                <Typography variant="h6" className="ml-3 mb-4">Product Tabs</Typography>
                <ColorSelectorGrid
                  value={values.product_page['theme_design/product_page/product_tabs_bg_color']}
                  name="product_page['theme_design/product_page/product_tabs_bg_color']"
                  label="Tab Background Color"
                  onChange={(color) => setFieldValue('product_page[\'theme_design/product_page/product_tabs_bg_color\']', color)}
                  InputProps={{
                    value: values.product_page['theme_design/product_page/product_tabs_bg_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.product_page['theme_design/product_page/product_tabs_color']}
                  name="product_page['theme_design/product_page/product_tabs_color']"
                  label="Tab Text Color"
                  onChange={(color) => setFieldValue('product_page[\'theme_design/product_page/product_tabs_color\']', color)}
                  InputProps={{
                    value: values.product_page['theme_design/product_page/product_tabs_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.product_page['theme_design/product_page/product_tabs_bg_color_active']}
                  name="product_page['theme_design/product_page/product_tabs_bg_color_active']"
                  label="Active Tab Background Color"
                  onChange={(color) => setFieldValue('product_page[\'theme_design/product_page/product_tabs_bg_color_active\']', color)}
                  InputProps={{
                    value: values.product_page['theme_design/product_page/product_tabs_bg_color_active'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.product_page['theme_design/product_page/product_tabs_color_active']}
                  name="product_page['theme_design/product_page/product_tabs_color_active']"
                  label="Active Tab Text Color"
                  onChange={(color) => setFieldValue('product_page[\'theme_design/product_page/product_tabs_color_active\']', color)}
                  InputProps={{
                    value: values.product_page['theme_design/product_page/product_tabs_color_active'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <Typography variant="h6" className="ml-3 mb-4">Product Promo</Typography>
                <ColorSelectorGrid
                  value={values.product_page['theme_design/product_page/product_promo_bg_color']}
                  name="product_page['theme_design/product_page/product_promo_bg_color']"
                  label="Promo Background Color"
                  onChange={(color) => setFieldValue('product_page[\'theme_design/product_page/product_promo_bg_color\']', color)}
                  InputProps={{
                    value: values.product_page['theme_design/product_page/product_promo_bg_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.product_page['theme_design/product_page/product_promo_color']}
                  name="product_page['theme_design/product_page/product_promo_color']"
                  label="Promo Text Color"
                  onChange={(color) => setFieldValue('product_page[\'theme_design/product_page/product_promo_color\']', color)}
                  InputProps={{
                    value: values.product_page['theme_design/product_page/product_promo_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.product_page['theme_design/product_page/product_promo_font_family']}
                  name="product_page['theme_design/product_page/product_promo_font_family']"
                  label="Promo Text Font Family"
                  onChange={(value) => setFieldValue('product_page[\'theme_design/product_page/product_promo_font_family\']', value)}
                  items={definedGoogleFontFamilies}
                />
                <FontSizeSelectorGrid
                  value={values.product_page['theme_design/product_page/product_promo_font_size']}
                  name="product_page['theme_design/product_page/product_promo_font_size']"
                  label="Promo Text Font Size"
                  onChange={(value) => setFieldValue('product_page[\'theme_design/product_page/product_promo_font_size\']', value)}
                />
                <TextFieldSelectorGrid
                  value={values.product_page['theme_design/product_page/product_promo_font_weight']}
                  name="product_page['theme_design/product_page/product_promo_font_weight']"
                  label="Promo Text Font Weight"
                  items={values.product_page['theme_design/product_page/product_promo_font_family']
                    ? getFontWeightParameters(
                      values.product_page['theme_design/product_page/product_promo_font_family'],
                      stylePalette.typography['theme_design/defined_fonts/google_fonts_string']
                    )
                    : [400]
                  }
                  onChange={(value) => setFieldValue('product_page[\'theme_design/product_page/product_promo_font_weight\']', value)}
                  InputProps={{
                    value: values.product_page['theme_design/product_page/product_promo_font_weight'],
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.product_page['theme_design/product_page/product_promo_line_height']}
                  name="product_page['theme_design/product_page/product_promo_line_height']"
                  label="Promo Text Line Height"
                  items={lineHeightValues}
                  onChange={(value) => setFieldValue('product_page[\'theme_design/product_page/product_promo_line_height\']', value)}
                  InputProps={{
                    value: values.product_page['theme_design/product_page/product_promo_line_height'],
                    shrink: 'true',
                  }}
                />
              </AccordionDetails>
            </Accordion>
            <Accordion
              elevation={25}
              className={`mt-4 border-radius-4 overflow-visible ${classes.expanded_index}`}
              expanded={expandedIndex === '12'}
              onChange={handleExpansionClick('12')}
            >
              <AccordionSummary
                expandIcon={<Icon>expand_more</Icon>}
              >
                <Typography variant="h5" className="flex items-center text-brand m-2">
                  <Icon className="mr-2">shopping_basket_icon</Icon>
                  Cart/Checkout Page
                </Typography>
              </AccordionSummary>
              <AccordionDetails className="flex-column">
                <Typography variant="h6" className="ml-3 mb-4">Promo Login Banner on Cart/Checkout</Typography>
                <ColorSelectorGrid
                  value={values.cart_page['theme_design/main/login_promo_bg_color']}
                  name="cart_page['theme_design/main/login_promo_bg_color']"
                  label="Promo Background Color"
                  onChange={(color) => setFieldValue('cart_page[\'theme_design/main/login_promo_bg_color\']', color)}
                  InputProps={{
                    value: values.cart_page['theme_design/main/login_promo_bg_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <ColorSelectorGrid
                  value={values.cart_page['theme_design/main/login_promo_color']}
                  name="cart_page['theme_design/main/login_promo_color']"
                  label="Promo Text Color"
                  onChange={(color) => setFieldValue('cart_page[\'theme_design/main/login_promo_color\']', color)}
                  InputProps={{
                    value: values.cart_page['theme_design/main/login_promo_color'],
                    readOnly: true,
                    shrink: 'true',
                  }}
                />
                <TextFieldSelectorGrid
                  value={values.cart_page['theme_design/main/login_promo_font_family']}
                  name="cart_page['theme_design/main/login_promo_font_family']"
                  label="Promo Text Font Family"
                  onChange={(value) => setFieldValue('cart_page[\'theme_design/main/login_promo_font_family\']', value)}
                  items={definedGoogleFontFamilies}
                />
                <TextFieldSelectorGrid
                  value={values.cart_page['theme_design/main/login_promo_text_transform']}
                  name="cart_page['theme_design/main/login_promo_text_transform']"
                  label="Promo Text Transform"
                  items={textTransformValues}
                  onChange={(value) => setFieldValue('cart_page[\'theme_design/main/login_promo_text_transform\']', value)}
                  InputProps={{
                    value: values.cart_page['theme_design/main/login_promo_text_transform'],
                    shrink: 'true',
                  }}
                />
                <FontSizeSelectorGrid
                  value={values.cart_page['theme_design/main/login_promo_title_font_size']}
                  name="cart_page['theme_design/main/login_promo_title_font_size']"
                  label="Promo Title Font Size"
                  items={[16, 18, 19, 20, 21, 22, 23, 24, 25, 26]}
                  onChange={(value) => setFieldValue('cart_page[\'theme_design/main/login_promo_title_font_size\']', value)}
                />
                <TextFieldSelectorGrid
                  value={values.cart_page['theme_design/main/login_promo_title_font_weight']}
                  name="cart_page['theme_design/main/login_promo_title_font_weight']"
                  label="Promo Title Font Weight"
                  items={values.cart_page['theme_design/main/login_promo_font_family']
                    ? getFontWeightParameters(
                      values.cart_page['theme_design/main/login_promo_font_family'],
                      stylePalette.typography['theme_design/defined_fonts/google_fonts_string']
                    )
                    : [400]
                  }
                  onChange={(value) => setFieldValue('cart_page[\'theme_design/main/login_promo_title_font_weight\']', value)}
                  InputProps={{
                    value: values.cart_page['theme_design/main/login_promo_title_font_weight'],
                    shrink: 'true',
                  }}
                />
                <FontSizeSelectorGrid
                  value={values.cart_page['theme_design/main/login_promo_desc_font_size']}
                  name="cart_page['theme_design/main/login_promo_desc_font_size']"
                  label="Promo Description Font Size"
                  onChange={(value) => setFieldValue('cart_page[\'theme_design/main/login_promo_desc_font_size\']', value)}
                />
              </AccordionDetails>
            </Accordion>
            {isPremium &&
              <Accordion
                elevation={25}
                className={`mt-4 border-radius-4 overflow-visible ${classes.expanded_index}`}
                expanded={expandedIndex === '13'}
                onChange={handleExpansionClick('13')}
              >
                <AccordionSummary
                  expandIcon={<Icon>expand_more</Icon>}
                >
                  <Typography variant="h5" className="flex items-center text-brand m-2">
                    <Icon className="mr-2">dashboard</Icon>
                    Homepage CTA
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className="flex-column">
                  <ColorSelectorGrid
                    value={values.homepage_cta['theme_design/cta/cta_vertical_title_color']}
                    name="homepage_cta['theme_design/cta/cta_vertical_title_color']"
                    label="CTA Vertical Block Title Color"
                    onChange={(color) => setFieldValue('homepage_cta[\'theme_design/cta/cta_vertical_title_color\']', color)}
                    InputProps={{
                      value: values.homepage_cta['theme_design/cta/cta_vertical_title_color'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.homepage_cta['theme_design/cta/cta_horizontal_top_title_color']}
                    name="homepage_cta['theme_design/cta/cta_horizontal_top_title_color']"
                    label="CTA Horizontal Top Block Title Color"
                    onChange={(color) => setFieldValue('homepage_cta[\'theme_design/cta/cta_horizontal_top_title_color\']', color)}
                    InputProps={{
                      value: values.homepage_cta['theme_design/cta/cta_horizontal_top_title_color'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.homepage_cta['theme_design/cta/cta_horizontal_bottom_title_color']}
                    name="homepage_cta['theme_design/cta/cta_horizontal_bottom_title_color']"
                    label="CTA Horizontal Bottom Block Title Color"
                    onChange={(color) => setFieldValue('homepage_cta[\'theme_design/cta/cta_horizontal_bottom_title_color\']', color)}
                    InputProps={{
                      value: values.homepage_cta['theme_design/cta/cta_horizontal_bottom_title_color'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <TextFieldSelectorGrid
                    value={values.homepage_cta['theme_design/cta/cta_title_font_weight']}
                    name="homepage_cta['theme_design/cta/cta_title_font_weight']"
                    label="CTA's Title Font Weight"
                    items={values.typography['theme_design/font/body_font_family']
                      ? getFontWeightParameters(
                        values.typography['theme_design/font/body_font_family'],
                        stylePalette.typography['theme_design/defined_fonts/google_fonts_string']
                      )
                      : [400]
                    }
                    onChange={(value) => setFieldValue('homepage_cta[\'theme_design/cta/cta_title_font_weight\']', value)}
                    InputProps={{
                      shrink: 'true',
                      value: values.homepage_cta['theme_design/cta/cta_title_font_weight']
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.homepage_cta['theme_design/cta/cta_mask_bg']}
                    name="homepage_cta['theme_design/cta/cta_mask_bg']"
                    label="CTA Mask Background"
                    onChange={(color) => setFieldValue('homepage_cta[\'theme_design/cta/cta_mask_bg\']', color)}
                    InputProps={{
                      value: values.homepage_cta['theme_design/cta/cta_mask_bg'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.homepage_cta['theme_design/cta/cta_mask_title_color']}
                    name="homepage_cta['theme_design/cta/cta_mask_title_color']"
                    label="CTA Mask Title Color"
                    onChange={(color) => setFieldValue('homepage_cta[\'theme_design/cta/cta_mask_title_color\']', color)}
                    InputProps={{
                      value: values.homepage_cta['theme_design/cta/cta_mask_title_color'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.homepage_cta['theme_design/cta/cta_mask_link_color']}
                    name="homepage_cta['theme_design/cta/cta_mask_link_color']"
                    label="CTA Mask Link Color"
                    onChange={(color) => setFieldValue('homepage_cta[\'theme_design/cta/cta_mask_link_color\']', color)}
                    InputProps={{
                      value: values.homepage_cta['theme_design/cta/cta_mask_link_color'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.homepage_cta['theme_design/cta/cta_mask_link_color_hover']}
                    name="homepage_cta['theme_design/cta/cta_mask_link_color_hover']"
                    label="CTA Mask Link Color Hover"
                    onChange={(color) => setFieldValue('homepage_cta[\'theme_design/cta/cta_mask_link_color_hover\']', color)}
                    InputProps={{
                      value: values.homepage_cta['theme_design/cta/cta_mask_link_color_hover'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.homepage_cta['theme_design/cta/cta_mask_button_bg']}
                    name="homepage_cta['theme_design/cta/cta_mask_button_bg']"
                    label="CTA Mask Button Background"
                    onChange={(color) => setFieldValue('homepage_cta[\'theme_design/cta/cta_mask_button_bg\']', color)}
                    InputProps={{
                      value: values.homepage_cta['theme_design/cta/cta_mask_button_bg'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.homepage_cta['theme_design/cta/cta_mask_button_color']}
                    name="homepage_cta['theme_design/cta/cta_mask_button_color']"
                    label="CTA Mask Button Text Color"
                    onChange={(color) => setFieldValue('homepage_cta[\'theme_design/cta/cta_mask_button_color\']', color)}
                    InputProps={{
                      value: values.homepage_cta['theme_design/cta/cta_mask_button_color'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.homepage_cta['theme_design/cta/cta_mask_button_bg_hover']}
                    name="homepage_cta['theme_design/cta/cta_mask_button_bg_hover']"
                    label="CTA Mask Button Background Hover"
                    onChange={(color) => setFieldValue('homepage_cta[\'theme_design/cta/cta_mask_button_bg_hover\']', color)}
                    InputProps={{
                      value: values.homepage_cta['theme_design/cta/cta_mask_button_bg_hover'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.homepage_cta['theme_design/cta/cta_mask_button_color_hover']}
                    name="homepage_cta['theme_design/cta/cta_mask_button_color_hover']"
                    label="CTA Mask Button Text Color Hover"
                    onChange={(color) => setFieldValue('homepage_cta[\'theme_design/cta/cta_mask_button_color_hover\']', color)}
                    InputProps={{
                      value: values.homepage_cta['theme_design/cta/cta_mask_button_color_hover'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <SwitchGrid
                    value={values.homepage_cta['theme_design/cta/button_border'] !== 'none'}
                    name="homepage_cta['theme_design/cta/button_border']"
                    label="Add CTA Mask Button Border?"
                    onChange={(value) => {
                      if (value == 1) {
                        setFieldValue('homepage_cta[\'theme_design/cta/button_border\']', '0px solid #fff');
                        setFieldValue('homepage_cta[\'theme_design/cta/button_border_hover\']', '0px solid #fff');
                      } else {
                        setFieldValue('homepage_cta[\'theme_design/cta/button_border\']', 'none');
                        setFieldValue('homepage_cta[\'theme_design/cta/button_border_hover\']', 'none');
                      }
                    }}
                  />
                  {values.homepage_cta['theme_design/cta/button_border'] !== 'none' &&
                  <>
                    <BorderSelectorGrid
                      value={values.homepage_cta['theme_design/cta/button_border']}
                      name="homepage_cta['theme_design/cta/button_border']"
                      label="CTA Mask Button Border"
                      setValue={(value) => setFieldValue('homepage_cta[\'theme_design/cta/button_border\']', value)}
                      InputProps={{
                        value: values.homepage_cta['theme_design/cta/button_border'],
                        shrink: 'true',
                      }}
                    />
                    <BorderSelectorGrid
                      value={values.homepage_cta['theme_design/cta/button_border_hover']}
                      name="homepage_cta['theme_design/cta/button_border_hover']"
                      label="CTA Mask Button Border Hover"
                      setValue={(value) => setFieldValue('homepage_cta[\'theme_design/cta/button_border_hover\']', value)}
                      InputProps={{
                        value: values.homepage_cta['theme_design/cta/button_border_hover'],
                        shrink: 'true',
                      }}
                    />
                  </>
                  }
                </AccordionDetails>
              </Accordion>
            }
            {isPremiumPlus &&
            <>
              <Accordion
                elevation={25}
                className={`mt-4 border-radius-4 overflow-visible ${classes.expanded_index}`}
                expanded={expandedIndex === '14'}
                onChange={handleExpansionClick('14')}
              >
                <AccordionSummary
                  expandIcon={<Icon>expand_more</Icon>}
                >
                  <Typography variant="h5" className="flex items-center text-brand m-2">
                    <Icon className="mr-2">dashboard</Icon>
                    CTA
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className="flex-column">
                  <ColorSelectorGrid
                    value={values.premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_title_color']}
                    name="premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_title_color']"
                    label="Title Color"
                    onChange={(color) => setFieldValue('premium_plus_cta[\'theme_design/premium_plus_homepage/premium_plus_cta_title_color\']', color)}
                    InputProps={{
                      value: values.premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_title_color'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <NumberFormatGrid
                    value={values.premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_title_font_size']}
                    suffix="px"
                    name={'premium_plus_cta[\'theme_design/premium_plus_homepage/premium_plus_cta_title_font_size\']'}
                    label="Title Font Size"
                    onChange={(value) => setFieldValue('premium_plus_cta[\'theme_design/premium_plus_homepage/premium_plus_cta_title_font_size\']', value)}
                    InputProps={{
                      shrink: 'true',
                    }}
                  />
                  <TextFieldSelectorGrid
                    value={values.premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_title_transform']}
                    name="premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_title_transform']"
                    label="Title Text Transform"
                    items={textTransformValues}
                    onChange={(value) => setFieldValue('premium_plus_cta[\'theme_design/premium_plus_homepage/premium_plus_cta_title_transform\']', value)}
                    InputProps={{
                      value: values.premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_title_transform'],
                      shrink: 'true',
                    }}
                  />
                  <TextFieldSelectorGrid
                    value={values.premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_title_font_weight']}
                    name="premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_title_font_weight']"
                    label="Title Font Weight"
                    items={values.typography['theme_design/font/body_font_family']
                      ? getFontWeightParameters(
                        values.typography['theme_design/font/body_font_family'],
                        stylePalette.typography['theme_design/defined_fonts/google_fonts_string']
                      )
                      : [400]
                    }
                    onChange={(value) => setFieldValue('premium_plus_cta[\'theme_design/premium_plus_homepage/premium_plus_cta_title_font_weight\']', value)}
                    InputProps={{
                      shrink: 'true',
                      value: values.premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_title_font_weight']
                    }}
                  />
                  <TextFieldSelectorGrid
                    value={values.premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_title_font_style']}
                    name="premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_title_font_style']"
                    label="Title Font Style"
                    items={fontStyleValues}
                    onChange={(value) => setFieldValue('premium_plus_cta[\'theme_design/premium_plus_homepage/premium_plus_cta_title_font_style\']', value)}
                    InputProps={{
                      value: values.premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_title_font_style'],
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_item_bg']}
                    name="premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_item_bg']"
                    label="Item Background Color"
                    onChange={(color) => setFieldValue('premium_plus_cta[\'theme_design/premium_plus_homepage/premium_plus_cta_item_bg\']', color)}
                    InputProps={{
                      value: values.premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_item_bg'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_text_color']}
                    name="premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_text_color']"
                    label="Item Text Color"
                    onChange={(color) => setFieldValue('premium_plus_cta[\'theme_design/premium_plus_homepage/premium_plus_cta_text_color\']', color)}
                    InputProps={{
                      value: values.premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_text_color'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <NumberFormatGrid
                    value={values.premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_text_font_size']}
                    suffix="px"
                    name={'premium_plus_cta[\'theme_design/premium_plus_homepage/premium_plus_cta_text_font_size\']'}
                    label="Item Text Font Size"
                    onChange={(value) => setFieldValue('premium_plus_cta[\'theme_design/premium_plus_homepage/premium_plus_cta_text_font_size\']', value)}
                    InputProps={{
                      shrink: 'true',
                    }}
                  />
                  <TextFieldSelectorGrid
                    value={values.premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_text_transform']}
                    name="premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_text_transform']"
                    label="Item Text Transform"
                    items={textTransformValues}
                    onChange={(value) => setFieldValue('premium_plus_cta[\'theme_design/premium_plus_homepage/premium_plus_cta_text_transform\']', value)}
                    InputProps={{
                      value: values.premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_text_transform'],
                      shrink: 'true',
                    }}
                  />
                  <TextFieldSelectorGrid
                    value={values.premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_text_font_weight']}
                    name="premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_text_font_weight']"
                    label="Item Font Weight"
                    items={values.typography['theme_design/font/body_font_family']
                      ? getFontWeightParameters(
                        values.typography['theme_design/font/body_font_family'],
                        stylePalette.typography['theme_design/defined_fonts/google_fonts_string']
                      )
                      : [400]
                    }
                    onChange={(value) => setFieldValue('premium_plus_cta[\'theme_design/premium_plus_homepage/premium_plus_cta_text_font_weight\']', value)}
                    InputProps={{
                      shrink: 'true',
                      value: values.premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_text_font_weight']
                    }}
                  />
                  <TextFieldSelectorGrid
                    value={values.premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_text_font_style']}
                    name="premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_text_font_style']"
                    label="Item Font Style"
                    items={fontStyleValues}
                    onChange={(value) => setFieldValue('premium_plus_cta[\'theme_design/premium_plus_homepage/premium_plus_cta_text_font_style\']', value)}
                    InputProps={{
                      value: values.premium_plus_cta['theme_design/premium_plus_homepage/premium_plus_cta_text_font_style'],
                      shrink: 'true',
                    }}
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion
                elevation={25}
                className={`mt-4 border-radius-4 overflow-visible ${classes.expanded_index}`}
                expanded={expandedIndex === '15'}
                onChange={handleExpansionClick('15')}
              >
                <AccordionSummary
                  expandIcon={<Icon>expand_more</Icon>}
                >
                  <Typography variant="h5" className="flex items-center text-brand m-2">
                    <Icon className="mr-2">local_activity_icon</Icon>
                    Testimonials
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className="flex-column">
                  <ColorSelectorGrid
                    value={values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_bg']}
                    name="premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_bg']"
                    label="Block Background Color"
                    onChange={(color) => setFieldValue('premium_plus_testimonials[\'theme_design/premium_plus_homepage/testimonials_bg\']', color)}
                    InputProps={{
                      value: values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_bg'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_text_color']}
                    name="premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_text_color']"
                    label="Content Color"
                    onChange={(color) => setFieldValue('premium_plus_testimonials[\'theme_design/premium_plus_homepage/testimonials_block_text_color\']', color)}
                    InputProps={{
                      value: values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_text_color'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <NumberFormatGrid
                    value={values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_text_font_size']}
                    suffix="px"
                    name={'premium_plus_testimonials[\'theme_design/premium_plus_homepage/testimonials_block_text_font_size\']'}
                    label="Content Font Size"
                    onChange={(value) => setFieldValue('premium_plus_testimonials[\'theme_design/premium_plus_homepage/testimonials_block_text_font_size\']', value)}
                    InputProps={{
                      shrink: 'true',
                    }}
                  />
                  <TextFieldSelectorGrid
                    value={values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_text_transform']}
                    name="premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_text_transform']"
                    label="Content Text Transform"
                    items={textTransformValues}
                    onChange={(value) => setFieldValue('premium_plus_testimonials[\'theme_design/premium_plus_homepage/testimonials_block_text_transform\']', value)}
                    InputProps={{
                      value: values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_text_transform'],
                      shrink: 'true',
                    }}
                  />
                  <TextFieldSelectorGrid
                    value={values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_text_font_weight']}
                    name="premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_text_font_weight']"
                    label="Content Font Weight"
                    items={values.typography['theme_design/font/body_font_family']
                      ? getFontWeightParameters(
                        values.typography['theme_design/font/body_font_family'],
                        stylePalette.typography['theme_design/defined_fonts/google_fonts_string']
                      )
                      : [400]
                    }
                    onChange={(value) => setFieldValue('premium_plus_testimonials[\'theme_design/premium_plus_homepage/testimonials_block_text_font_weight\']', value)}
                    InputProps={{
                      shrink: 'true',
                      value: values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_text_font_weight']
                    }}
                  />
                  <TextFieldSelectorGrid
                    value={values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_text_font_style']}
                    name="premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_text_font_style']"
                    label="Content Font Style"
                    items={fontStyleValues}
                    onChange={(value) => setFieldValue('premium_plus_testimonials[\'theme_design/premium_plus_homepage/testimonials_block_text_font_style\']', value)}
                    InputProps={{
                      value: values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_text_font_style'],
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_rating_bg_color']}
                    name="premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_rating_bg_color']"
                    label="Rating Background Color"
                    onChange={(color) => setFieldValue('premium_plus_testimonials[\'theme_design/premium_plus_homepage/testimonials_block_rating_bg_color\']', color)}
                    InputProps={{
                      value: values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_rating_bg_color'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_rating_color']}
                    name="premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_rating_color']"
                    label="Rating Color"
                    onChange={(color) => setFieldValue('premium_plus_testimonials[\'theme_design/premium_plus_homepage/testimonials_block_rating_color\']', color)}
                    InputProps={{
                      value: values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_rating_color'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_author_color']}
                    name="premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_author_color']"
                    label="Author Text Color"
                    onChange={(color) => setFieldValue('premium_plus_testimonials[\'theme_design/premium_plus_homepage/testimonials_block_author_color\']', color)}
                    InputProps={{
                      value: values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_author_color'],
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <NumberFormatGrid
                    value={values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_author_font_size']}
                    suffix="px"
                    name={'premium_plus_testimonials[\'theme_design/premium_plus_homepage/testimonials_block_author_font_size\']'}
                    label="Author Text Font Size"
                    onChange={(value) => setFieldValue('premium_plus_testimonials[\'theme_design/premium_plus_homepage/testimonials_block_author_font_size\']', value)}
                    InputProps={{
                      shrink: 'true',
                    }}
                  />
                  <TextFieldSelectorGrid
                    value={values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_author_transform']}
                    name="premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_author_transform']"
                    label="Author Text Transform"
                    items={textTransformValues}
                    onChange={(value) => setFieldValue('premium_plus_testimonials[\'theme_design/premium_plus_homepage/testimonials_block_author_transform\']', value)}
                    InputProps={{
                      value: values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_author_transform'],
                      shrink: 'true',
                    }}
                  />
                  <TextFieldSelectorGrid
                    value={values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_author_font_weight']}
                    name="premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_author_font_weight']"
                    label="Author Font Weight"
                    items={values.typography['theme_design/font/body_font_family']
                      ? getFontWeightParameters(
                        values.typography['theme_design/font/body_font_family'],
                        stylePalette.typography['theme_design/defined_fonts/google_fonts_string']
                      )
                      : [400]
                    }
                    onChange={(value) => setFieldValue('premium_plus_testimonials[\'theme_design/premium_plus_homepage/testimonials_block_author_font_weight\']', value)}
                    InputProps={{
                      shrink: 'true',
                      value: values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_author_font_weight']
                    }}
                  />
                  <TextFieldSelectorGrid
                    value={values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_author_font_style']}
                    name="premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_author_font_style']"
                    label="Author Font Style"
                    items={fontStyleValues}
                    onChange={(value) => setFieldValue('premium_plus_testimonials[\'theme_design/premium_plus_homepage/testimonials_block_author_font_style\']', value)}
                    InputProps={{
                      value: values.premium_plus_testimonials['theme_design/premium_plus_homepage/testimonials_block_author_font_style'],
                      shrink: 'true',
                    }}
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion
                elevation={25}
                className={`mt-4 border-radius-4 overflow-visible ${classes.expanded_index}`}
                expanded={expandedIndex === '16'}
                onChange={handleExpansionClick('16')}
              >
                <AccordionSummary
                  expandIcon={<Icon>expand_more</Icon>}
                >
                  <Typography variant="h5" className="flex items-center text-brand m-2">
                    <Icon className="mr-2">book</Icon>
                    Blog
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className="flex-column">
                  <ColorSelectorGrid
                    value={values.premium_plus_blog['theme_design/premium_plus_homepage/blog_block_bg'] || 'inherit'}
                    name="premium_plus_blog['theme_design/premium_plus_homepage/blog_block_bg']"
                    label="Main Background Color"
                    onChange={(color) => setFieldValue('premium_plus_blog[\'theme_design/premium_plus_homepage/blog_block_bg\']', color)}
                    InputProps={{
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.premium_plus_blog['theme_design/premium_plus_homepage/blog_block_main_heading_color'] || 'inherit'}
                    name="premium_plus_blog['theme_design/premium_plus_homepage/blog_block_main_heading_color']"
                    label="Main Heading Color"
                    onChange={(color) => setFieldValue('premium_plus_blog[\'theme_design/premium_plus_homepage/blog_block_main_heading_color\']', color)}
                    InputProps={{
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <NumberFormatGrid
                    value={values.premium_plus_blog['theme_design/premium_plus_homepage/blog_block_main_heading_font_size'] || 'inherit'}
                    suffix="px"
                    name={'premium_plus_blog[\'theme_design/premium_plus_homepage/blog_block_main_heading_font_size\']'}
                    label="Main Heading Font Size"
                    onChange={(value) => setFieldValue('premium_plus_blog[\'theme_design/premium_plus_homepage/blog_block_main_heading_font_size\']', value)}
                    InputProps={{
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.premium_plus_blog['theme_design/premium_plus_homepage/blog_block_text_color'] || 'inherit'}
                    name="premium_plus_blog['theme_design/premium_plus_homepage/blog_block_text_color']"
                    label="Text Color"
                    onChange={(color) => setFieldValue('premium_plus_blog[\'theme_design/premium_plus_homepage/blog_block_text_color\']', color)}
                    InputProps={{
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <NumberFormatGrid
                    value={values.premium_plus_blog['theme_design/premium_plus_homepage/blog_block_text_font_size'] || 'inherit'}
                    suffix="px"
                    name={'premium_plus_blog[\'theme_design/premium_plus_homepage/blog_block_text_font_size\']'}
                    label="Text Font Size"
                    onChange={(value) => setFieldValue('premium_plus_blog[\'theme_design/premium_plus_homepage/blog_block_text_font_size\']', value)}
                    InputProps={{
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.premium_plus_blog['theme_design/premium_plus_homepage/blog_block_button_bg'] || 'inherit'}
                    name="premium_plus_blog['theme_design/premium_plus_homepage/blog_block_button_bg']"
                    label="Button Background Color"
                    onChange={(color) => setFieldValue('premium_plus_blog[\'theme_design/premium_plus_homepage/blog_block_button_bg\']', color)}
                    InputProps={{
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.premium_plus_blog['theme_design/premium_plus_homepage/blog_block_button_color'] || 'inherit'}
                    name="premium_plus_blog['theme_design/premium_plus_homepage/blog_block_button_color']"
                    label="Button Text Color"
                    onChange={(color) => setFieldValue('premium_plus_blog[\'theme_design/premium_plus_homepage/blog_block_button_color\']', color)}
                    InputProps={{
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.premium_plus_blog['theme_design/premium_plus_homepage/blog_block_button_border_color'] || 'inherit'}
                    name="premium_plus_blog['theme_design/premium_plus_homepage/blog_block_button_border_color']"
                    label="Button Border Color"
                    onChange={(color) => setFieldValue('premium_plus_blog[\'theme_design/premium_plus_homepage/blog_block_button_border_color\']', color)}
                    InputProps={{
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.premium_plus_blog['theme_design/premium_plus_homepage/blog_block_button_bg_hover'] || 'inherit'}
                    name="premium_plus_blog['theme_design/premium_plus_homepage/blog_block_button_bg_hover']"
                    label="Button Background Color Hover"
                    onChange={(color) => setFieldValue('premium_plus_blog[\'theme_design/premium_plus_homepage/blog_block_button_bg_hover\']', color)}
                    InputProps={{
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.premium_plus_blog['theme_design/premium_plus_homepage/blog_block_button_color_hover'] || 'inherit'}
                    name="premium_plus_blog['theme_design/premium_plus_homepage/blog_block_button_color_hover']"
                    label="Button Text Color Hover"
                    onChange={(color) => setFieldValue('premium_plus_blog[\'theme_design/premium_plus_homepage/blog_block_button_color_hover\']', color)}
                    InputProps={{
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion
                elevation={25}
                className={`mt-4 border-radius-4 overflow-visible ${classes.expanded_index}`}
                expanded={expandedIndex === '17'}
                onChange={handleExpansionClick('17')}
              >
                <AccordionSummary
                  expandIcon={<Icon>expand_more</Icon>}
                >
                  <Typography variant="h5" className="flex items-center text-brand m-2">
                    <InstagramIcon className="mr-2" />
                    Instagram
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className="flex-column">
                  <ColorSelectorGrid
                    value={values.premium_plus_instagram['theme_design/premium_plus_homepage/instagram_block_mask_bg'] || 'inherit'}
                    name="premium_plus_instagram['theme_design/premium_plus_homepage/instagram_block_mask_bg']"
                    label="Mask Background Color"
                    onChange={(color) => setFieldValue('premium_plus_instagram[\'theme_design/premium_plus_homepage/instagram_block_mask_bg\']', color)}
                    InputProps={{
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.premium_plus_instagram['theme_design/premium_plus_homepage/instagram_block_main_heading_color'] || 'inherit'}
                    name="premium_plus_instagram['theme_design/premium_plus_homepage/instagram_block_main_heading_color']"
                    label="Main Heading Color"
                    onChange={(color) => setFieldValue('premium_plus_instagram[\'theme_design/premium_plus_homepage/instagram_block_main_heading_color\']', color)}
                    InputProps={{
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <NumberFormatGrid
                    value={values.premium_plus_instagram['theme_design/premium_plus_homepage/instagram_block_main_heading_font_size'] || 'inherit'}
                    suffix="px"
                    name={'premium_plus_instagram[\'theme_design/premium_plus_homepage/instagram_block_main_heading_font_size\']'}
                    label="Main Heading Font Size"
                    onChange={(value) => setFieldValue('premium_plus_instagram[\'theme_design/premium_plus_homepage/instagram_block_main_heading_font_size\']', value)}
                    InputProps={{
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.premium_plus_instagram['theme_design/premium_plus_homepage/instagram_block_button_bg'] || 'inherit'}
                    name="premium_plus_instagram['theme_design/premium_plus_homepage/instagram_block_button_bg']"
                    label="Button Background Color"
                    onChange={(color) => setFieldValue('premium_plus_instagram[\'theme_design/premium_plus_homepage/instagram_block_button_bg\']', color)}
                    InputProps={{
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.premium_plus_instagram['theme_design/premium_plus_homepage/instagram_block_button_color'] || 'inherit'}
                    name="premium_plus_instagram['theme_design/premium_plus_homepage/instagram_block_button_color']"
                    label="Button Text Color"
                    onChange={(color) => setFieldValue('premium_plus_instagram[\'theme_design/premium_plus_homepage/instagram_block_button_color\']', color)}
                    InputProps={{
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.premium_plus_instagram['theme_design/premium_plus_homepage/instagram_block_button_border_color'] || 'inherit'}
                    name="premium_plus_instagram['theme_design/premium_plus_homepage/instagram_block_button_border_color']"
                    label="Button Border Color"
                    onChange={(color) => setFieldValue('premium_plus_instagram[\'theme_design/premium_plus_homepage/instagram_block_button_border_color\']', color)}
                    InputProps={{
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.premium_plus_instagram['theme_design/premium_plus_homepage/instagram_block_button_bg_hover'] || 'inherit'}
                    name="premium_plus_instagram['theme_design/premium_plus_homepage/instagram_block_button_bg_hover']"
                    label="Button Background Color Hover"
                    onChange={(color) => setFieldValue('premium_plus_instagram[\'theme_design/premium_plus_homepage/instagram_block_button_bg_hover\']', color)}
                    InputProps={{
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                  <ColorSelectorGrid
                    value={values.premium_plus_instagram['theme_design/premium_plus_homepage/instagram_block_button_color_hover'] || 'inherit'}
                    name="premium_plus_instagram['theme_design/premium_plus_homepage/instagram_block_button_color_hover']"
                    label="Button Text Color Hover"
                    onChange={(color) => setFieldValue('premium_plus_instagram[\'theme_design/premium_plus_homepage/instagram_block_button_color_hover\']', color)}
                    InputProps={{
                      readOnly: true,
                      shrink: 'true',
                    }}
                  />
                </AccordionDetails>
              </Accordion>
            </>
            }
          </div>
          <div className="flex justify-end w-full">
            <FormControlLabel
              control={
                <Checkbox
                  checked={isSaveAsDefault}
                  onChange={(e) => setSaveAsDefault(e.target.checked)}
                  name="isSaveAsDefault"
                  color="primary"
                />
              }
              label="Save as default styles"
            />
          </div>
          <div className="flex items-center justify-between">
            <Button
              variant="contained"
              onClick={() => {
                handleUpdateStylePalette(values);
                onStepChange(1);
              }}
              title="Next"
              className="px-6 bg-dark"
            >
              <ChevronDoubleLeft />
              <span className="ml-2 text-16">Back</span>
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              title="Next"
              className="px-6"
            >
              Save Configurations
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default AdvancedConfigurations;
